<template>
  <div class="d-flex">
    <div id="wrapper" class="d-flex">
      <div id="page-content-wrapper d-flex">
        <nav
          class="
            navbar navbar-expand-lg navbar-light
            bg-na-principal
            border-bottom
            fixed-top
            custom-navbar custom-navbar-extended
          "
          :class="{ 'mobile-responsive': isMobile }"
        >
          <the-hamburger v-if="isMobile" />
          <div class="sidebar-heading cursor-pointer">
            <img
              alt="logo"
              class="cursor-pointer app-logo"
              height="60px"
              :src="logo"
              width="230px"
            />
          </div>
          <div
            id="navbarSupportedContent"
            class="collapse navbar-collapse show float-right left"
          >
            <ul
              class="navbar-nav mr-auto mt-0 mt-lg-0 d-flex align-items-center"
            >
              <li>
                <droppable
                  class="action-item-search"
                  :show="true"
                  @click="onOpenSearchStudentHandler"
                >
                  <div class="media align-items-center">
                    <span
                      class="
                        avatar avatar-sm
                        bg-gray-7
                        border-none
                        rounded-circle
                      "
                    >
                      <em class="fas fa-search font-20" />
                    </span>
                  </div>
                  <template #drop>
                    <student-search
                      ref="studentSearchRef"
                      :can-select-student="hideHistoryAndSelectAction"
                      :can-view-history="hideHistoryAndSelectAction"
                      :get-advanced-search="getAdvancedSearch"
                      :get-impediments="getImpediments"
                      :get-student-by-sie="getStudentBySie"
                      :is-mobile="isMobile"
                      :student-history-list="studentHistoryList"
                      @on-select-student="onSelectStudentHandler"
                      @on-view-student="onViewStudentHandler"
                    />
                  </template>
                </droppable>
              </li>
              <li class="nav-item no-arrow mr-4">
                <custom-user-info
                  class="ml-3 custom-user-info"
                  :last-login-date="new Date(userInfo.lastlogindate)"
                  :row-actions="userCard.rowActions"
                  :row-elements="userCard.rowElements"
                />
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </div>
    <modal-password
      class="modal-password__extended"
      :show="isUserPasswordChangeVisible"
      @on-close="isUserPasswordChangeVisible = false"
      @submit="handleChangePassword"
    />
    <student-search-info
      :academic-years="academicYears"
      class="student-search-info__extended"
      :pei-history-data="peiHistoryData"
      :show="showStudentInfo"
      :show-therapy-tab="false"
      :status-list="studentStatuses"
      :student-info="studentInfo"
      @on-close="() => (showStudentInfo = false)"
      @on-search-pei-history="onSearchPeiHistoryHandler"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import CustomUserInfo from 'CustomUserInfoPanel';
import TheHamburger from './TheHamburger.vue';
import StudentSearch from 'student-search';
import StudentSearchInfo from 'student-search-info';
import Droppable from 'custom-droppable';
import { name as appModuleName } from '@/store/modules/app';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import { userMixin } from '@/mixins/user.mixin';
import { name as studentModuleName } from '@/store/modules/student';
import {
  getBySie as _getBySie,
  getImpediments as _getImpediments,
  getAdvancedSearch as _getAdvancedSearch,
  getStudentDetailsWithEnrollmentSchool as _getStudentDetailsWithEnrollmentSchool,
  getStudentPeiHistory as _getStudentPeiHistory,
  getStudentStatuses as _getStudentStatuses,
  saveHistorySearch as _saveHistorySearch,
} from '@/services/common/student.api.js';
import ModalPassword from '@quisit/mipe-modal-password';

import { getPeiAcademicYears as _getPeiAcademicYears } from '@/services/common/academicyear.api.js';
import studentStatuses from '@/utils/constants/studentStatuses.js';
import { changePassword as _changePassword } from '@/core/auth';

const PROV_REMEDY_ROUTE_NAME = 'prov-remedy-req-management-add';
const DEFAULT_PEI_HISTORY_PAGE_SIZE = 10;

export default {
  name: 'TheHeader',
  mixins: [alertMixin, userMixin],
  components: {
    TheHamburger,
    Droppable,
    CustomUserInfo,
    StudentSearch,
    StudentSearchInfo,
    ModalPassword,
  },
  props: {
    userInfo: {
      type: Object,
      default: () => ({}),
    },
    excludeHelpCard: {
      type: Array,
      default: () => [], //array strings, para excluir items de caja de ayuda
    },
    contactInfo: {
      type: Object,
      default: () => ({
        phone: '787-773-6147',
        mail: 'apoyoconversion@de.pr.gov',
      }),
    },
    logo: {
      type: String,
      required: true,
    },
    logout: {
      type: Function,
      required: true,
    },
  },
  watch: {
    isSearchStudentOpenedFromOutside(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$refs.studentSearchRef.showModal();
      }
    },
  },
  data() {
    return {
      isUserPasswordChangeVisible: false,
      showStudentInfo: null,
      studentInfo: {},
      peiHistoryData: {},
      academicYears: [],
      studentStatuses: [],
    };
  },
  computed: {
    ...mapState(studentModuleName, [
      'isSearchStudentOpenedFromOutside',
      'hideHistoryAndSelectAction',
    ]),
    ...mapGetters(appModuleName, {
      isMobile: 'getIsMobileDensity',
      isNavOpened: 'getIsNavOpen',
    }),
    ...mapGetters(studentModuleName, {
      studentHistoryList: 'getSearchedStudentHistory',
    }),
    displayChangeUserPassword() {
      return this.isUserPasswordChangeVisible;
    },
    userCard() {
      return {
        rowElements: [
          {
            text: this.userInfo.role,
            icon: 'fas fa-key',
            colorIcon: 'orange',
            color: 'none',
            withBorder: false,
          },
          {
            text: this.userInfo.name,
            icon: 'fas fa-user',
            colorIcon: 'orange',
            color: 'none',
            withBorder: false,
          },
          {
            text: this.userInfo.email,
            icon: 'fas fa-envelope',
            colorIcon: 'orange',
            color: 'none',
            withBorder: false,
          },
        ],
        rowActions: [
          {
            text: 'Mi Perfil',
            icon: 'fas fa-address-card',
            colorIcon: 'green',
            color: 'green',
            withBorder: true,
          },
          {
            text: 'Cambiar Contraseña',
            icon: 'fas fa-repeat-alt',
            colorIcon: 'green',
            color: 'green',
            withBorder: true,
            click: this.ShowHidePasswordChange,
          },
          {
            text: 'Salir',
            icon: 'fas fa-sign-out-alt',
            colorIcon: 'green',
            color: 'green',
            withBorder: true,
            click: () => this.logout(),
          },
        ],
      };
    },
    helpCard() {
      let rowElements = [
        { text: 'Manual', icon: 'fas fa-book' },
        { text: 'Videos', icon: 'fas fa-video' },
      ];
      return {
        rowElements: rowElements.filter(
          (item) => !this.excludeHelpCard.includes(item.text)
        ),
      };
    },
  },
  async mounted() {
    await this.getUserStudentSearchedHistory();
  },
  methods: {
    ...mapActions(studentModuleName, ['getUserStudentSearchedHistory']),
    ...mapMutations(studentModuleName, ['setHideHistoryAndSelectStudent']),
    ShowHidePasswordChange() {
      this.isUserPasswordChangeVisible = true;
    },
    getStudentBySie(studentSieId, webServiceSearchOnly) {
      return _getBySie(studentSieId, webServiceSearchOnly);
    },
    getImpediments() {
      return _getImpediments();
    },
    getAdvancedSearch(payload) {
      return _getAdvancedSearch(payload);
    },
    async onOpenSearchStudentHandler() {
      this.setHideHistoryAndSelectStudent(false);
      this.$refs.studentSearchRef.showModal();
    },
    getStudentStatusName(statusId) {
      switch (statusId) {
        case studentStatuses.Available:
          return 'Disponible';
        case studentStatuses.Inactive:
          return 'Inactivo';
        case studentStatuses.NotEligible:
          return 'No Elegible';
        case studentStatuses.Egress:
          return 'Egreso';

        default:
          return '';
      }
    },
    async onSelectStudentHandler({ studentId, statusId }) {
      if (
        [
          studentStatuses.Available,
          studentStatuses.Inactive,
          studentStatuses.NotEligible,
          studentStatuses.Egress,
        ].includes(statusId)
      ) {
        this.ShowToast(
          'Alerta',
          `No es posible añadir una solicitud ya que el estudiante se encuentra en estatus ${this.getStudentStatusName(
            statusId
          )}`,
          'error'
        );
        return;
      }

      //save selection in student history
      await _saveHistorySearch(studentId);
      await this.getUserStudentSearchedHistory(true);

      this.$refs.studentSearchRef.hideModal();
      if (studentId) {
        this.$router.push({
          name: PROV_REMEDY_ROUTE_NAME,
          params: {
            studentSieId: studentId,
          },
        });
      }
    },
    async getPeiHistory(studentSieId, year = 0, pageIndex = 1) {
      const result = (
        await _getStudentPeiHistory(
          studentSieId,
          year,
          pageIndex,
          DEFAULT_PEI_HISTORY_PAGE_SIZE
        )
      ).data;

      this.peiHistoryData = {
        ...result,
        pageSize: DEFAULT_PEI_HISTORY_PAGE_SIZE,
      };
    },
    async onViewStudentHandler({ studentId }) {
      this.studentInfo = {};
      this.$refs.studentSearchRef.hideModal();
      if (studentId) {
        this.showStudentInfo = true;

        //get student enrollent school info
        this.studentInfo = (
          await _getStudentDetailsWithEnrollmentSchool(studentId)
        ).data;

        //get pei history data
        await this.getPeiHistory(studentId);

        //get academic years list
        this.academicYears = (await _getPeiAcademicYears()).data;

        //get student statuses
        const studentStatusCode =
          +this.studentInfo?.mipeStudent?.studentStatusCode || null;
        this.studentStatuses = (
          await _getStudentStatuses(studentStatusCode)
        ).data;
      }
    },
    async onSearchPeiHistoryHandler(academicYear, studentSieId, pageIndex) {
      if (studentSieId) {
        await this.getPeiHistory(studentSieId, academicYear, pageIndex);
      }
    },
    async handleChangePassword(e) {
      let payload = {
        currentPassword: e.currentPassword,
        confirmPassword: e.confirmPassword,
        newPassword: e.newPassword,
      };

      const { data } = await _changePassword(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });

      if (!data.error) {
        this.isUserPasswordChangeVisible = false;

        this.ShowToast(
          'Proceso exitoso.',
          'La contraseña ha sido cambiada exitosamente.',
          'success',
          5000
        );

        return;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modal-password__extended,
.student-search-info__extended ::v-deep {
  div.modal-mask {
    z-index: 9999 !important;
  }
}
.div-help {
  margin-left: -10px;
  color: #ff7058;
}
.help-text {
  margin-left: -10px;
}
.help-information {
  margin-left: -10px !important;
}
.information-text {
  margin-left: -7px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
}
.support-table {
  background: #f2f2f2 0% 0% no-repeat padding-box;
  opacity: 1;
}
.support-information {
  text-align: left;
  font: Bold 16px/22px Lato;
  letter-spacing: 0;
  color: #20a874 !important;
  opacity: 1;
}
.support-label {
  margin-left: 11px !important;
  text-decoration: underline;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
}
.shadow-top {
  -moz-box-shadow: inset 0 10px 10px -10px grey;
  -webkit-box-shadow: inset 0 10px 10px -10px grey;
  box-shadow: inset 0 10px 10px -10px grey;
}
.alert-indicator {
  position: absolute;
  top: 32px;
  right: 157px;
  background: #fd3737 0% 0% no-repeat padding-box;
  color: #fff;
  width: 15px;
  height: 15px;
  opacity: 1;
  line-height: 15px;
  text-align: center;
  font-size: 10px;
}

.dropdown-menu-custom {
  top: 95% !important;
}

.nav-item .media {
  margin-left: 20px;
}

.nav-item .media em {
  font-size: 1.225rem;
}

.user-panel {
  margin-left: 20px;
}

.custom-navbar-extended {
  box-shadow: 0 0.3rem 1rem rgba(0, 0, 0, 0.15);
  z-index: 5;
  &--is-normal {
    right: 0px !important;
  }
}
.custom-user-info ::v-deep {
  .user-panel {
    .dropdown-menu {
      top: 80px;
      margin-right: 1.59rem;
      min-width: 15rem;
    }
  }
}

@media screen and (min-width: 768px) and (max-width: 991px) {
  .custom-user-info ::v-deep > .user-panel > .dropdown-menu {
    top: 4.6rem;
    margin-right: 2.35rem;
    min-width: 15rem;
  }

  ul.navbar-nav {
    height: 55px;
    width: 62%;
  }

  ul.navbar-nav li:nth-child(1) {
    margin-right: 2.3rem;
  }
  ul.navbar-nav li:nth-child(2) {
    margin-top: -4.2rem;
  }
}

@media screen and (max-width: 767px) {
  .custom-user-info ::v-deep > .user-panel > .dropdown-menu {
    top: 5rem;
    margin-right: 1.1rem;
    min-width: 15rem;
  }
}
</style>
